import React, { useState, useEffect } from 'react';
import { X, Users, Plus, Search, CheckCircle2, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const BookGroupModal = ({ myGroups = [], onClose, onGroupAction }) => {
  const { user, profile } = useAuth();
  const [view, setActiveView] = useState('find');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const [applyMessage, setApplyMessage] = useState('');
  const [applyingTo, setApplyingTo] = useState(null);

  useEffect(() => {
    if (view === 'find') fetchGroups();
  }, [view]);

  const fetchGroups = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('book_groups')
      .select('*')
      .eq('status', 'active')
      .order('created_at', { ascending: false });
    if (error) console.error(error);
    if (data) setGroups(data);
    setLoading(false);
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newName || !user) return;

    if (!profile?.username || !profile.username.trim()) {
      alert("You must set a username in your Profile before establishing a group/circle!");
      return;
    }

    setLoading(true);
    
    try {
      // 1. Create Group
      const { data: groupData, error: groupError } = await supabase
        .from('book_groups')
        .insert([{ 
          name: newName, 
          description: newDesc, 
          created_by: user.id,
          role_names: { owner: "Owner", admin: "Admin", moderator: "Moderator", member: "Member" }
        }])
        .select();

      if (groupError) throw groupError;

      // 2. Auto-join as owner
      const { error: memberError } = await supabase.from('group_members').insert([{
        group_id: groupData[0].id,
        user_id: user.id,
        role: 'owner'
      }]);

      if (memberError) throw memberError;

      alert(`Group "${newName}" established!`);
      if (onGroupAction) onGroupAction();
      onClose();
    } catch (err) {
      alert("Error creating group: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (groupId) => {
    if (!user) return alert("Please log in to join groups.");

    if (!profile?.username || !profile.username.trim()) {
      alert("You must set a username in your Profile before applying to join a circle!");
      return;
    }

    setLoading(true);
    
    const { error } = await supabase.rpc('apply_to_group', { 
      target_group_id: groupId, 
      join_message: applyMessage 
    });

    if (error) {
      if (error.code === '23505') alert("You have already applied to this group!");
      else alert("Error applying: " + error.message);
    } else {
      alert("Application sent to the sanctuary leaders!");
      setApplyingTo(null);
      setApplyMessage('');
    }
    setLoading(false);
  };

  return (
    <div style={modalOverlay}>
      <div style={modalContent}>
        <div style={header}>
          <div style={{ display: 'flex', gap: '15px' }}>
            <button onClick={() => setActiveView('find')} style={view === 'find' ? activeTab : tab}>Find</button>
            <button onClick={() => setActiveView('create')} style={view === 'create' ? activeTab : tab}>Create</button>
          </div>
          <button onClick={onClose} style={closeBtn}><X size={20} /></button>
        </div>

        <div style={scrollArea}>
          {view === 'find' ? (
            <div>
              <div style={searchBar}>
                <Search size={18} color="var(--text-soft)" />
                <input 
                  placeholder="Search groups..." 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={searchInput}
                />
              </div>

              {loading ? <p style={msg}>Loading...</p> : (
                <div style={groupList}>
                  {groups
                    .filter(g => g.name.toLowerCase().includes(search.toLowerCase()))
                    .filter(g => !g.is_private || myGroups.some(mg => mg.id === g.id))
                    .map(g => {
                      const isMember = myGroups.some(mg => mg.id === g.id);
                      const myMemberInfo = myGroups.find(mg => mg.id === g.id);
                      const myRole = myMemberInfo ? (myMemberInfo.group_members?.[0]?.role || 'member') : null;
                      const customRoleName = myMemberInfo && myMemberInfo.role_names?.[myRole] ? myMemberInfo.role_names[myRole] : myRole;

                      return (
                        <div key={g.id} style={groupItem}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', color: 'var(--text-main)' }}>{g.name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-soft)' }}>{g.description}</div>
                            
                            {applyingTo === g.id && (
                              <div style={{ marginTop: '10px' }}>
                                <textarea 
                                  placeholder="Why do you want to join?"
                                  value={applyMessage}
                                  onChange={(e) => setApplyMessage(e.target.value)}
                                  style={applyInput}
                                />
                                <div style={{ display: 'flex', gap: '8px', marginTop: '5px' }}>
                                  <button onClick={() => handleApply(g.id)} style={confirmApplyBtn}>Send Application</button>
                                  <button onClick={() => setApplyingTo(null)} style={cancelApplyBtn}>Cancel</button>
                                </div>
                              </div>
                            )}
                          </div>
                          {isMember ? (
                            <span style={joinedBadgeStyle}>
                              {customRoleName || 'Joined'}
                            </span>
                          ) : g.accepting_applications === false ? (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-soft)', fontStyle: 'italic' }}>Closed</span>
                          ) : (
                            !applyingTo && <button onClick={() => setApplyingTo(g.id)} style={joinBtn}>Apply</button>
                          )}
                        </div>
                    );
                  })}
                  {groups.length === 0 && <p style={msg}>No groups found. Be the first to start one!</p>}
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleCreateGroup} style={form}>
              <div style={field}>
                <label style={label}>Group Name</label>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Midnight Readers" style={input} required />
              </div>
              <div style={field}>
                <label style={label}>Description</label>
                <textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="What is this group about?" style={{ ...input, height: '80px', resize: 'none' }} />
              </div>
              <button type="submit" disabled={loading} style={submitBtn}>
                {loading ? <Loader2 className="spin" size={20} /> : 'Establish Group'}
              </button>
            </form>
          )}
        </div>
      </div>
      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const modalOverlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 4000, padding: '20px'
};
const modalContent = { backgroundColor: 'var(--bg-card)', width: '100%', maxWidth: '450px', borderRadius: '24px', display: 'flex', flexDirection: 'column', maxHeight: '80vh' };
const header = { padding: '20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const tab = { background: 'none', border: 'none', padding: '8px 0', fontSize: '0.9rem', color: 'var(--text-soft)', cursor: 'pointer', borderBottom: '2px solid transparent', fontWeight: 'bold' };
const activeTab = { ...tab, color: 'var(--accent)', borderBottomColor: 'var(--accent)' };
const scrollArea = { padding: '20px', overflowY: 'auto' };
const searchBar = { display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: 'var(--bg-app)', padding: '10px 15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid var(--border)' };
const searchInput = { background: 'none', border: 'none', color: 'var(--text-main)', flex: 1, outline: 'none', fontSize: '0.9rem' };
const groupList = { display: 'flex', flexDirection: 'column', gap: '12px' };
const groupItem = { padding: '15px', backgroundColor: 'var(--bg-app)', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '15px', border: '1px solid var(--border)' };
const joinBtn = { padding: '6px 16px', backgroundColor: 'var(--accent)', color: 'white', border: 'none', borderRadius: '20px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem' };
const joinedBadgeStyle = { padding: '6px 14px', backgroundColor: 'var(--accent-soft)', color: 'var(--accent)', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase' };
const applyInput = { width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-card)', color: 'var(--text-main)', fontSize: '0.85rem', marginBottom: '8px', resize: 'none', boxSizing: 'border-box' };
const confirmApplyBtn = { padding: '6px 12px', backgroundColor: 'var(--accent)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.75rem' };
const cancelApplyBtn = { padding: '6px 12px', backgroundColor: 'var(--bg-card)', color: 'var(--text-soft)', border: '1px solid var(--border)', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.75rem' };
const form = { display: 'flex', flexDirection: 'column', gap: '15px' };
const field = { display: 'flex', flexDirection: 'column', gap: '6px' };
const label = { fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-soft)', textTransform: 'uppercase' };
const input = { padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-app)', color: 'var(--text-main)', fontSize: '0.9rem' };
const submitBtn = { marginTop: '10px', padding: '14px', backgroundColor: 'var(--accent)', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' };
const closeBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-soft)' };
const msg = { textAlign: 'center', color: 'var(--text-soft)', padding: '20px', fontSize: '0.9rem' };

export default BookGroupModal;
